import { useMemo } from "react";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { makeUrl, routes } from "../../lib/urls";
import { useMonitoringInteraction } from "./analytics";
import { DonutChart } from "../DonutChart";
import {
  buildSpaceSegments,
  OTHER_SPACES_KEY,
  type SpaceSegmentRow,
} from "./spaceSegments";
import { Text } from "../Text";
import { InfoCorner, PanelBadge } from "./PanelInfo";
import { spaceColorMap } from "./monitoringPanelLayout";
import {
  foldsPersonal,
  PERSONAL_BUCKET,
  PERSONAL_BUCKET_COLOR,
} from "./monitoringScope";
import { useMonitoringScope } from "./useMonitoringScope";
import {
  useMonitoringSpaces,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

export function ActiveWorkflowsBySpace() {
  const recordInteraction = useMonitoringInteraction();
  const { report, loading } = useMonitoringWorkflows();
  const { spaces, loading: spacesLoading } = useMonitoringSpaces();
  const { scope, personalIds, matchesSpace } = useMonitoringScope(
    routes.monitoring.workflows
  );
  const displayName = useSpaceDisplayName();

  const workflows = useMemo(
    () => report.workflows.filter((workflow) => workflow.active),
    [report]
  );

  const { chartRows, total } = useMemo(() => {
    const countBySpace = new Map<string, number>();
    for (const workflow of workflows) {
      countBySpace.set(
        workflow.spaceId,
        (countBySpace.get(workflow.spaceId) ?? 0) + 1
      );
    }

    const chartRows: SpaceSegmentRow[] = [];
    let personalCount = 0;
    for (const space of spaces) {
      if (!matchesSpace(space.id)) continue;
      const count = countBySpace.get(space.id) ?? 0;
      if (count === 0) continue;
      if (foldsPersonal(scope) && personalIds.has(space.id)) {
        personalCount += count;
        continue;
      }
      chartRows.push({ id: space.id, label: displayName(space), value: count });
    }
    if (personalCount > 0) {
      chartRows.push({
        id: PERSONAL_BUCKET,
        label: "Personal",
        value: personalCount,
      });
    }
    chartRows.sort(
      (a, b) => b.value - a.value || a.label.localeCompare(b.label)
    );
    const total = chartRows.reduce((sum, row) => sum + row.value, 0);
    return { chartRows, total };
  }, [workflows, spaces, matchesSpace, displayName, scope, personalIds]);

  const colorMap = useMemo(() => {
    const map = new Map(spaceColorMap(spaces));
    map.set(PERSONAL_BUCKET, PERSONAL_BUCKET_COLOR);
    return map;
  }, [spaces]);

  const segments = useMemo(
    () => buildSpaceSegments(chartRows, total, colorMap),
    [chartRows, total, colorMap]
  );

  if (loading || spacesLoading) {
    return <PanelSkeleton />;
  }

  return (
    <Panel>
      <InfoCorner>
        <PanelBadge>Last 30 days</PanelBadge>
      </InfoCorner>
      <PanelHeader>
        <Text variant="text-small-md">Active workflows by space</Text>
        <TotalRow>
          <Text variant="display-md">{total}</Text>
        </TotalRow>
      </PanelHeader>
      <DonutChart
        segments={segments}
        total={total}
        placeholder={total === 0}
        strokeWidth={6}
        centerCaption="workflows"
        scrollableLegend
        segmentHref={(s) =>
          s.key === PERSONAL_BUCKET || s.key === OTHER_SPACES_KEY
            ? undefined
            : makeUrl(routes.space, { spaceId: s.key })
        }
        onSegmentNavigate={(s) =>
          recordInteraction("workflows.space_link_click", {
            targetType: "space",
            targetId: s.key,
          })
        }
      />
    </Panel>
  );
}
